import { Component, OnInit } from '@angular/core';
import { Bone, BoneStatus, Clue, HuntApiService } from '../hunt-api.service';
import { accessMessage } from '../hunt-errors';

@Component({
  selector: 'app-hunt-bones',
  templateUrl: './hunt-bones.component.html',
  styleUrls: ['./hunt-bones.component.scss'],
})
export class HuntBonesComponent implements OnInit {
  readonly filters: { key: BoneStatus | 'all'; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'live', label: 'Live' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'unscheduled', label: 'Not scheduled' },
    { key: 'found', label: 'Found' },
    { key: 'missed', label: 'Missed' },
  ];
  filter: BoneStatus | 'all' = 'all';
  search = '';
  bones: Bone[] = [];
  loading = true;
  error = '';
  notice = '';

  open: number | null = null; // expanded bone
  edit: { clues: Clue[]; reveal: string; spaceId: string } | null = null;
  saving = false;

  constructor(private api: HuntApiService) {}

  ngOnInit() { this.load(); }

  load() {
    this.loading = true;
    this.api.bones().subscribe(
      (r) => { this.bones = r.bones; this.loading = false; },
      (e) => { this.error = accessMessage(e); this.loading = false; },
    );
  }

  count(key: BoneStatus | 'all') { return key === 'all' ? this.bones.length : this.bones.filter((b) => b.status === key).length; }

  get shown(): Bone[] {
    const q = this.search.trim().toLowerCase();
    return this.bones.filter((b) =>
      (this.filter === 'all' || b.status === this.filter) &&
      (!q || `${b.n} ${b.room ? `${b.room.spaceName} ${b.room.building} ${b.room.locality} ${b.room.city} ${b.room.spaceId}` : ''}`.toLowerCase().includes(q)));
  }

  editable(b: Bone) { return b.status === 'unscheduled' || b.status === 'scheduled'; }

  toggle(b: Bone) {
    this.open = this.open === b.n ? null : b.n;
    this.edit = null;
    this.notice = '';
  }

  startEdit(b: Bone) {
    this.edit = { clues: b.clues.map((c) => ({ ...c })), reveal: b.reveal || '', spaceId: b.room ? b.room.spaceId : '' };
  }
  addClue() { this.edit.clues.push({ after: (this.edit.clues[this.edit.clues.length - 1] || { after: 0 }).after + 30, text: '' }); }
  removeClue(i: number) { this.edit.clues.splice(i, 1); }

  save(b: Bone) {
    const changes: any = { clues: this.edit.clues.filter((c) => c.text.trim()), reveal: this.edit.reveal.trim() || null };
    if (b.room && this.edit.spaceId.trim() && this.edit.spaceId.trim() !== b.room.spaceId) changes.spaceId = this.edit.spaceId.trim();
    if (!changes.clues.length) { this.error = 'A bone needs at least one clue.'; return; }
    this.saving = true;
    this.error = '';
    this.api.updateBone(b.n, changes).subscribe(
      (nb) => {
        this.bones = this.bones.map((x) => (x.n === nb.n ? nb : x));
        this.saving = false;
        this.edit = null;
        this.notice = `Bone #${nb.n} saved.`;
      },
      (e) => { this.saving = false; this.error = accessMessage(e); },
    );
  }

  remove(b: Bone) {
    if (!confirm(`Delete bone #${b.n} (${b.room ? b.room.spaceName : ''})? This can't be undone.`)) return;
    this.api.deleteBone(b.n).subscribe(
      () => { this.bones = this.bones.filter((x) => x.n !== b.n); this.open = null; this.notice = `Bone #${b.n} deleted.`; },
      (e) => (this.error = accessMessage(e)),
    );
  }

  /** Download the current list as CSV (for records or re-importing). */
  exportCsv() {
    const q = (v: any) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const lines = ['bone,space_id,room,building,locality,city,status,opens_ist,closes_ist,clue,clue2,clue2_after,clue3,clue3_after,reveal,found_code'];
    const ist = (t: number | null) => (t ? new Date(t).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '');
    for (const b of this.bones) {
      const c = b.clues;
      lines.push([b.n, b.room && b.room.spaceId, b.room && b.room.spaceName, b.room && b.room.building, b.room && b.room.locality, b.room && b.room.city,
        b.status, ist(b.opensAt), ist(b.closesAt), c[0] && c[0].text, c[1] && c[1].text, c[1] && c[1].after, c[2] && c[2].text, c[2] && c[2].after,
        b.reveal, b.claim && b.claim.code].map(q).join(','));
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = 'spark-bones.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }
}
